/** @format */

import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Dropdown, DropdownMenu, DropdownItem, _$, Icon } from '@king-design/react'
import Divider from '../Divider'
import variable from 'src/static/styles/var'

export interface TableActionItem {
  /** 唯一标识 */
  key: string
  /** 按钮文案 */
  label: React.ReactNode
  /** 点击回调 */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void
  /** 为 true 时不渲染 */
  hidden?: boolean
  /** 禁用时阻止点击，并应用 disabled-text 样式 */
  disabled?: boolean
  /** 额外 class，如 disabled-text */
  className?: string
}

export interface TableActionProps {
  className?: string
  /** 操作项列表，顺序即展示顺序；危险操作建议放在最后 */
  actions: TableActionItem[]
  /** 直接展示的文字按钮上限，默认 3 */
  maxVisible?: number
  /** 超出上限时的下拉入口文案 */
  moreText?: string
}

const DEFAULT_MAX_VISIBLE = 3

const TableAction: React.FC<TableActionProps> = ({
  className,
  actions,
  maxVisible = DEFAULT_MAX_VISIBLE,
  moreText = _$('更多')
}) => {
  const visibleActions = useMemo(() => actions.filter(action => !action.hidden), [actions])

  const directActions = visibleActions.slice(0, maxVisible)
  const overflowActions = visibleActions.slice(maxVisible)

  const handleClick = (action: TableActionItem, e: React.MouseEvent<HTMLElement>) => {
    if (action.disabled) {
      e.preventDefault()
      return
    }
    action.onClick?.(e)
  }

  const nodes: React.ReactNode[] = []

  directActions.forEach((action, index) => {
    if (index > 0) {
      nodes.push(<Divider key={`divider-${action.key}`} />)
    }
    nodes.push(
      <a
        key={action.key}
        className={
          [action.disabled ? 'disabled-text' : '', action.className].filter(Boolean).join(' ') ||
          undefined
        }
        onClick={e => handleClick(action, e)}
      >
        {action.label}
      </a>
    )
  })

  if (overflowActions.length > 0) {
    if (directActions.length > 0) {
      nodes.push(<Divider key="divider-more" />)
    }
    nodes.push(
      <Dropdown key="more" trigger="click">
        <a className="table-action__more">
          {moreText}
          <Icon className="k-icon-down" />
        </a>
        <DropdownMenu>
          {overflowActions.map(action => (
            <DropdownItem
              key={action.key}
              disabled={action.disabled}
              onClick={() => {
                if (!action.disabled) {
                  action.onClick?.({} as React.MouseEvent<HTMLElement>)
                }
              }}
            >
              {action.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    )
  }

  if (nodes.length === 0) {
    return null
  }

  return (
    <div className={['ksc-space', 'table-action', className].filter(Boolean).join(' ')}>
      {nodes}
    </div>
  )
}

export default styled(TableAction)`
  flex-wrap: nowrap;
  white-space: nowrap;

  .table-action__more {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 2px;
    .k-icon {
      color: ${variable.primaryColor};
    }
  }
`
